"use client";

import { createContext, useCallback, useContext, useEffect, useRef, useState, ReactNode } from "react";
import { Check, AlertCircle, Info, X } from "lucide-react";
import { cn } from "@/lib/utils";

// ──────────────────────────────────────────────────────────────────────────
// Toast 시스템 — Undo 패턴 지원
// ──────────────────────────────────────────────────────────────────────────

export type ToastKind = "info" | "success" | "error";

type ToastInput = {
  message: string;
  kind?: ToastKind;
  /** 자동 dismiss 시간 (ms). 기본 3000. action 있으면 5000 */
  duration?: number;
  action?: {
    label: string;
    onClick: () => void | Promise<void>;
  };
};

type ToastState = ToastInput & { id: number; visible: boolean };

type Ctx = {
  show: (t: ToastInput) => void;
  dismiss: () => void;
};

const ToastCtx = createContext<Ctx>({ show: () => {}, dismiss: () => {} });

export function useToast() {
  return useContext(ToastCtx);
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toast, setToast] = useState<ToastState | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const idRef = useRef(0);

  const dismiss = useCallback(() => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    setToast((t) => (t ? { ...t, visible: false } : null));
    // exit 애니메이션 후 언마운트
    setTimeout(() => setToast(null), 220);
  }, []);

  const show = useCallback(
    (t: ToastInput) => {
      if (timer.current) clearTimeout(timer.current);
      const id = ++idRef.current;
      const duration = t.duration ?? (t.action ? 5000 : 3000);
      // 잠깐 null로 만들어 줘서 중복 시 fade in 트리거
      setToast({ ...t, id, visible: true });
      timer.current = setTimeout(() => {
        setToast((cur) => (cur && cur.id === id ? { ...cur, visible: false } : cur));
        setTimeout(() => setToast((cur) => (cur && cur.id === id ? null : cur)), 220);
      }, duration);
    },
    [],
  );

  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current);
  }, []);

  return (
    <ToastCtx.Provider value={{ show, dismiss }}>
      {children}
      {toast && (
        <div
          className="pointer-events-none fixed inset-x-0 z-[200] flex justify-center px-4"
          style={{
            // 하단 네비를 피해서 위로
            bottom: "calc(env(safe-area-inset-bottom) + 96px)",
          }}
        >
          <div
            role="status"
            className={cn(
              "pointer-events-auto flex w-full max-w-[420px] items-center gap-2.5 rounded-2xl border bg-[var(--surface)] px-3.5 py-2.5 shadow-2xl shadow-black/40 transition-all duration-200",
              toast.kind === "error" && "border-red-500/30 bg-red-950/70",
              toast.kind === "success" && "border-emerald-500/30",
              (!toast.kind || toast.kind === "info") && "border-[var(--border)]",
              toast.visible
                ? "translate-y-0 opacity-100"
                : "translate-y-4 opacity-0 pointer-events-none",
            )}
          >
            <span className="flex h-7 w-7 shrink-0 items-center justify-center">
              {toast.kind === "success" && <Check size={16} className="text-emerald-400" />}
              {toast.kind === "error" && <AlertCircle size={16} className="text-red-400" />}
              {(!toast.kind || toast.kind === "info") && <Info size={16} className="text-[var(--accent)]" />}
            </span>
            <div className="flex-1 text-[13px] leading-snug text-[var(--foreground)]">
              {toast.message}
            </div>
            {toast.action && (
              <button
                onClick={async () => {
                  try {
                    await toast.action!.onClick();
                  } finally {
                    dismiss();
                  }
                }}
                className="shrink-0 rounded-full bg-[var(--accent)]/20 px-3 py-1 text-[12px] font-semibold text-[var(--accent)] active:opacity-70"
              >
                {toast.action.label}
              </button>
            )}
            <button
              onClick={dismiss}
              aria-label="닫기"
              className="shrink-0 rounded-full p-1 text-[var(--muted)] active:text-[var(--foreground)]"
            >
              <X size={14} />
            </button>
          </div>
        </div>
      )}
    </ToastCtx.Provider>
  );
}
